import { Reminder } from '@griply/types';
import { IconFormNames } from 'shared/assets/icons';
import {
  CompletionTargetUnit,
  FrequencyUnit,
  HabitSchedule,
  RruleReferenceDate,
  ScheduleCompletionActions,
} from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { reminderSchema } from 'shared/types/reminder';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import * as yup from 'yup';

export type NewHabit = {
  name: string;
  iconName: IconFormNames | null;
  type: TaskType.Habit | TaskType.Repeating;
  hideOnTodo?: boolean | null;
  goalIds?: ID[] | null;
  goalId?: ID | null;
  lifeAreaId?: ID | null;
  tagIds?: ID[] | null;
  priority?: TaskPriorityOptions | null;
  deadline?: Timestamp | null;
  reminderTime?: number | null;
  reminders?: Reminder[] | null;
  startTime?: number | null;
  duration?: number | null;
  schedules?: HabitSchedule[];
};

export const validationSchema: yup.ObjectSchema<NewHabit> = yup.object({
  name: yup.string().trim().required(),
  type: yup.string().oneOf([TaskType.Habit, TaskType.Repeating]).required(),
  hideOnTodo: yup.bool().nullable(),
  iconName: yup
    .string<IconFormNames>()
    .oneOf(Object.values(IconFormNames))
    .required(),
  goalIds: yup.array().of(yup.string().required()).nullable(),
  goalId: yup.string().nullable(),
  lifeAreaId: yup.string().nullable(),
  tagIds: yup.array().of(yup.string().required()),
  deadline: yup.date().nullable(),
  reminderTime: yup.number().nullable(),
  reminders: yup.array().of(reminderSchema.required()).nullable(),
  startTime: yup.number().nullable(),
  duration: yup.number().nullable(),
  priority: yup.string().oneOf(Object.values(TaskPriorityOptions)).nullable(),
  schedules: yup
    .array(
      yup.object({
        startDate: yup.date().required(),
        rrule: yup
          .object({
            format: yup.string().required(),
            referenceDate: yup
              .string<RruleReferenceDate>()
              .oneOf(Object.values(RruleReferenceDate))
              .required(),
          })
          .required(),
        frequency: yup
          .object({
            count: yup.number().required(),
            unit: yup
              .string<FrequencyUnit>()
              .oneOf(Object.values(FrequencyUnit))
              .required(),
          })
          .nullable(),
        completionTarget: yup.object({
          count: yup.number().required(),
          unit: yup
            .string<CompletionTargetUnit>()
            .oneOf(Object.values(CompletionTargetUnit))
            .required(),
        }),
        action: yup
          .object({
            type: yup
              .string<ScheduleCompletionActions>()
              .oneOf(Object.values(ScheduleCompletionActions))
              .required(),
          })
          .nullable()
          .default(null),
        entries: yup.array().required(),
        endDate: yup.date().nullable(),
      }),
    )
    .required(),
});

export type HabitFormFields = yup.InferType<typeof validationSchema>;
